import {
  DateRange,
  DateRangeInput,
  DATE_FORMATTER_STR,
  formatDate,
  Header,
  NotApplicable,
  ScrollContainer,
  Search,
  Select,
  ToggleButtonOption,
  ToggleButtons,
  useLoading,
  ValueOpt,
  isAfter,
  Filters,
  useBreakpoints,
  useLocalStorageState,
  addWeeks,
} from "best-common-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { getAdminRequests, getAdminRequestsBySeries } from "../../../api/RequesTixApi";
import AdminRequestsView from "../../../components/requests/admin/AdminRequestsView";
import AdminRequestSeries from "../../../components/requests/admin/bySeries/AdminRequestSeries";
import LocalStorageConstants from "../../../constants/LocalStorageConstants";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { AdminRequestsDTO, AdminRequestSeriesDTO } from "../../../types/AdminRequest";
import { RequestCategory } from "../../../types/RequestType";
import { todayStr } from "../../../util/DateObjUtil";
import { getCategoryOption, getCompleteOption } from "../../../util/DropdownUtil";
import { getTeamOption } from "../../../util/TeamUtil";
import { requestsFreeTextFilter } from "../../../util/TicketRequestUtil";
interface AdminRequestsFilters {
  startDate: string;
  endDate?: string;
  /** When true, search uses [startDate, endDate]; when false, search uses [startDate, startDate]. */
  dateRangeEnabled: boolean;
  homeTeamId: string | undefined;
  statusId: string;
  categoryId: string | undefined;
  completeId: string | undefined;
  search: string | undefined;
  view: string;
  requestId: string | undefined;
}

const DEFAULT_FILTERS: AdminRequestsFilters = {
  startDate: todayStr,
  endDate: undefined,
  dateRangeEnabled: false,
  homeTeamId: undefined,
  statusId: "",
  categoryId: undefined,
  completeId: undefined,
  search: undefined,
  view: "1",
  requestId: undefined,
};

const toggleOpts: ToggleButtonOption<string>[] = [
  { value: "2", label: "Series/Game" },
  { value: "1", label: "Requests" },
];

const defaultByRequests: AdminRequestsDTO = {
  schedule: [],
  games: {},
  requests: {},
  totalRequests: {},
  gameList: [],
  ticketRequests: [],
};

const AdminRequests = () => {
  const location = useLocation();
  const { setLoading } = useLoading();
  const { isMobile } = useBreakpoints();
  const { requestCategoryOptions, requestStateFilterOptions, teamsOptions, completeOptions } = useDropdowns();
  const [byRequests, setByRequests] = useState<AdminRequestsDTO>(defaultByRequests);
  const [byRequestsFiltered, setByRequestsFiltered] = useState({});
  const [bySeries, setBySeries] = useState<AdminRequestSeriesDTO[]>([]);

  const [filters, setFilters, localStorageLoaded] = useLocalStorageState<AdminRequestsFilters>(
    LocalStorageConstants.ADMIN_REQUESTS_FILTERS,
    DEFAULT_FILTERS,
  );

  const {
    startDate,
    endDate,
    dateRangeEnabled: dateRangeEnabledRaw,
    homeTeamId,
    statusId,
    categoryId,
    completeId,
    search,
    view,
    requestId,
  } = filters;
  const dateRangeEnabled = dateRangeEnabledRaw ?? true;

  const setFilter = useCallback(
    <K extends keyof AdminRequestsFilters>(key: K, value: AdminRequestsFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [setFilters],
  );

  const isRequests = useMemo(() => view === "1", [view]);
  const homeTeam = useMemo(() => getTeamOption(homeTeamId, teamsOptions), [homeTeamId, teamsOptions]);
  const category = useMemo(
    () => getCategoryOption(categoryId ?? "", requestCategoryOptions),
    [categoryId, requestCategoryOptions],
  );
  const complete = useMemo(() => getCompleteOption(completeId ?? "", completeOptions), [completeId, completeOptions]);
  const dateRange: DateRange<Date> = useMemo(
    () => ({
      start: new Date(Date.parse(startDate!)),
      end: endDate ? new Date(Date.parse(endDate)) : undefined,
    }),
    [startDate, endDate],
  );
  const isValidDateRange = useMemo(() => {
    return (dateRangeEnabled && endDate ? endDate : startDate) == startDate || !isAfter(startDate!, endDate!);
  }, [dateRangeEnabled, startDate, endDate]);

  const handleRangeToggleChange = useCallback(
    (rangeEnabled: boolean) => {
      setFilter("dateRangeEnabled", rangeEnabled);
      if (!rangeEnabled) {
        setFilter("endDate", undefined);
      } else {
        const defaultEndStr = addWeeks(Date.parse(startDate!), 2) as Date;
        setFilter("endDate", formatDate(defaultEndStr, DATE_FORMATTER_STR));
      }
    },
    [setFilter],
  );

  const getByRequests = useCallback(async () => {
    try {
      if (isValidDateRange) {
        setByRequests(defaultByRequests);
        setLoading(true);
        const data: AdminRequestsDTO = await getAdminRequests(
          startDate!,
          dateRangeEnabled ? endDate! : startDate,
          homeTeamId ? Number(homeTeamId) : 0,
          statusId ? Number(statusId) : 0,
          categoryId ? Number(categoryId) : 0,
          completeId ? Number(completeId) : 0,
        );
        setByRequests(data);
      }
    } catch (e) {
      console.error(e);
      setByRequests(defaultByRequests);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, homeTeamId, statusId, categoryId, completeId, setLoading, isValidDateRange]);

  const getBySeries = useCallback(async () => {
    try {
      if (isValidDateRange) {
        setLoading(true);
        const data: AdminRequestSeriesDTO[] = await getAdminRequestsBySeries(
          startDate!,
          dateRangeEnabled ? endDate! : startDate,
          homeTeamId ? Number(homeTeamId) : undefined,
          statusId ? Number(statusId) : undefined,
          completeId ? Number(completeId) : undefined,
        );
        setBySeries(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, homeTeamId, statusId, completeId, setLoading, isValidDateRange]);

  const updateDate = useCallback(
    (dateRange: DateRange<Date> | NotApplicable) => {
      const dr = dateRange as DateRange<Date>;
      if (!dr?.start) {
        return;
      }
      const startStr = formatDate(dr.start, DATE_FORMATTER_STR);
      setFilters((prev) => {
        const hasEnd = !!dr.end;
        return {
          ...prev,
          startDate: startStr,
          endDate: hasEnd ? formatDate(dr.end!, DATE_FORMATTER_STR) : undefined,
          dateRangeEnabled: hasEnd,
        };
      });
    },
    [setFilters],
  );

  useEffect(() => {
    if (!localStorageLoaded) {
      return;
    }
    if (isRequests) {
      void getByRequests();
    } else {
      void getBySeries();
    }
  }, [localStorageLoaded, isRequests, getByRequests, getBySeries]);

  useEffect(() => {
    setByRequestsFiltered(requestsFreeTextFilter(byRequests.requests, search ?? ""));
  }, [byRequests, search]);

  return (
    <div>
      <Header sticky title="Ticket Requests" flexColumn>
        <Filters
          numberDisplayed={4}
          indexesToDisplay={isMobile ? [1] : undefined}
          showExpansionButtons={true}
          actionIconPosition="absolute"
        >
          <ToggleButtons
            id="display-by"
            className="w-100"
            label="Display By"
            value={view}
            options={toggleOpts}
            onChange={(value?: string) => {
              if (value !== undefined) setFilter("view", value);
            }}
            gutterBottom
          />
          <div style={{ ["--bcr-checkbox-font-weight" as string]: "bold" } as React.CSSProperties}>
            <DateRangeInput
              key={localStorageLoaded ? "requests-date" : "requests-date-pending"}
              id="requests-date"
              label="Date"
              value={dateRange}
              onChange={updateDate}
              maxRange={
                !!homeTeamId
                  ? {
                      months: 1,
                    }
                  : {
                      weeks: 2,
                    }
              }
              rangeToggle
              defaultRangeChecked={dateRangeEnabled}
              onRangeToggleChange={handleRangeToggleChange}
              collapsed
              withPortal
              gutterBottom
            />
          </div>
          <Select
            id="complete"
            label="Complete/Incomplete"
            value={complete}
            options={completeOptions}
            onChange={(value: ValueOpt<{ id?: number }>) =>
              setFilter("completeId", value?.value?.id ? String(value.value.id) : undefined)
            }
            placeholder="select complete or incomplete"
            gutterBottom
          />
          <Select
            id="home-team"
            label="Home Team"
            value={homeTeam}
            options={teamsOptions}
            onChange={(value) => setFilter("homeTeamId", value?.value?.id ? String(value.value.id) : undefined)}
            placeholder="select a home team"
            gutterBottom
          />
          {isRequests ? (
            <Search
              id="search"
              label="Search Requests"
              value={search}
              onChange={(value: string) => setFilter("search", value || undefined)}
              placeholder="begin typing to filter requests"
              gutterBottom
            />
          ) : (
            <div />
          )}
          {isRequests ? (
            <Select
              id="status"
              label="Status"
              options={requestStateFilterOptions}
              value={requestStateFilterOptions.find((option: ValueOpt<number[]>) => option.value.join() === statusId)}
              onChange={(value: ValueOpt<number[]>) => setFilter("statusId", value.value.join())}
              placeholder="select a status"
              gutterBottom
            />
          ) : (
            <div />
          )}
          {isRequests ? (
            <Select
              id="category"
              label="Category"
              value={category}
              options={requestCategoryOptions}
              onChange={(value: ValueOpt<RequestCategory>) =>
                setFilter(
                  "categoryId",
                  value?.value?.requestCategoryId ? String(value.value.requestCategoryId) : undefined,
                )
              }
              placeholder="select a category"
              gutterBottom
            />
          ) : (
            <div />
          )}
        </Filters>
      </Header>
      <ScrollContainer className="bcr-p-2">
        {isRequests ? (
          <AdminRequestsView
            {...byRequests}
            requests={byRequestsFiltered}
            startDate={startDate}
            jumpToRequestId={requestId ? Number(requestId) : undefined}
          />
        ) : (
          <AdminRequestSeries values={bySeries} location={location} />
        )}
      </ScrollContainer>
    </div>
  );
};

export default AdminRequests;
